/* 216 §6 [RULED 75] — ONE WINDOW TO FILE IN, for a dropped document: the e-mail attachments window's browser
   (FolderBrowser.tsx — the breadcrumb, the subfolders, « Nouveau dossier », and now the destinations at its top: the
   matter's folder or the folder of any of the matter's clients whose folder the viewer may see) opened on its own,
   in OneDrivePicker.tsx's manner (a promise, a root of its own, settled once), where the drop asked the general
   SharePoint browser until now — that one had no « Nouveau dossier » and « Remonter » stopped at the matter's folder
   (Laurent's screenshots of 09/10). The general browser stays for what it does elsewhere: picking a file, linking a
   folder [ADVISOR]. The words come through the imperative resolver (the window mounts outside the app's tree, as
   the picker does). */
import React from "react";
import { useEffect, useState } from "react";
import type { Dispatch } from "react";
import ReactDOM from "react-dom/client";
import { api } from "../api";
import { tImperative } from "../i18n";
import { ModalLayout } from "./SharedUI";
import { FS, Z } from "../lib/tokens";
import FolderBrowser, { MATTER_ROOT, type FileRoots } from "./FolderBrowser";
import type { MatterKind } from "./MatterPicker";
import type { Translate } from "../lib/interestRefusal";

/** What the window answers: the root, the folder under it (segments joined — the upload's `subPath`), its name. */
export interface FileDestination {
  root: string;
  subPath: string;
  folderName: string | null;
}

interface FileDestinationModalProps {
  kind: MatterKind;
  id: number;
  /** the viewer holds `caseView.documents.edit` on the matter (the upload's gate — the drop asked it already) */
  canCreateFolder: boolean;
  onPick: Dispatch<FileDestination>;
  onCancel: () => void;
}

const t: Translate = (key, vars) => String(tImperative(key, vars));

function FileDestinationModal({ kind, id, canCreateFolder, onPick, onCancel }: Readonly<FileDestinationModalProps>) {
  const base = `${kind === "cc" ? "/collective-cases" : "/cases"}/${String(id)}/files`;
  const [path, setPath] = useState<string[]>([]);
  const [root, setRoot] = useState<string>(MATTER_ROOT);
  const [roots, setRoots] = useState<FileRoots | null>(null);
  useEffect(() => {
    let cancelled = false;
    void api<FileRoots | null>(`${base}/roots`)
      .then((d) => {
        if (!cancelled) setRoots(d ?? null);
      })
      .catch(() => {
        if (!cancelled) setRoots(null);
      });
    return () => {
      cancelled = true;
    };
  }, [base]);
  const folderName = path.length ? path[path.length - 1] : null;
  return (
    <ModalLayout
      title={t("fileWhere.title", { fallback: "Where to file" })}
      onClose={onCancel}
      showX={false}
      maxWidth={560}
      zIndex={Z.modal2}
      footer={
        <div className="modal-footer">
          <button type="button" className="modal-cancel" onClick={onCancel}>
            {t("common.cancel", { fallback: "Cancel" })}
          </button>
          <button
            type="button"
            className="modal-save"
            data-file-here=""
            onClick={() => {
              onPick({ root, subPath: path.join("/"), folderName });
            }}
          >
            {t("fileWhere.file_here", { fallback: "File here" })}
          </button>
        </div>
      }
    >
      <div style={{ padding: "12px 16px 4px", fontSize: FS.s11 }}>
        <FolderBrowser
          t={t}
          base={`${base}/folders`}
          path={path}
          onPath={setPath}
          canCreateFolder={canCreateFolder}
          roots={roots}
          root={root}
          onRoot={setRoot}
          searchClients={kind === "cc"}
        />
      </div>
    </ModalLayout>
  );
}

/** The window as a promise: the destination chosen, or null when closed. One root of its own, settled once. */
export function openFileDestination(opts: { kind: MatterKind; id: number; canCreateFolder: boolean }): Promise<FileDestination | null> {
  return new Promise((resolve) => {
    let settled = false;
    const container = globalThis.document.createElement("div");
    container.setAttribute("data-lx-file-destination", "");
    globalThis.document.body.appendChild(container);
    const root = ReactDOM.createRoot(container);
    const cleanup = () => {
      try {
        root.unmount();
      } catch {
        /* already gone */
      }
      try {
        container.remove();
      } catch {
        /* already gone */
      }
    };
    const settle = (value: FileDestination | null) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };
    root.render(
      <FileDestinationModal
        kind={opts.kind}
        id={opts.id}
        canCreateFolder={opts.canCreateFolder}
        onPick={settle}
        onCancel={() => {
          settle(null);
        }}
      />,
    );
  });
}
